import type {
  CalculatedAttributesCollector,
  CollectedAttributeGroup,
} from '../../calculatedAttributes/collect';
import {
  NO_VERTICAL,
  VERTICAL_BY_MODE,
} from '../../calculatedAttributes/moduleMode';
import type { TravelTripCriteria } from '../../types/travelInsurance';
import { ComponentTableBuilder } from './types';

/**
 * The trip this table is authored for, or `undefined` when it is not a Travel
 * Insurance table. Separate from the collector because the two questions differ:
 * the collector answers "does this table RENDER a quoted value", while a value
 * can also be needed by something the table does not draw — the ItemList JSON-LD
 * prices the products a table lists without the table showing a price column
 * (CMS-1955). Both read the criteria here, so the trip has one reader.
 */
export const getTableBuilderTripCriteria = (
  component: ComponentTableBuilder,
): TravelTripCriteria | undefined => {
  if ((component.mode ?? 'GENERAL') !== 'TRAVEL_INSURANCE') return undefined;
  return {
    insuranceType: component.defaultInsuranceType,
    travelAsFamily: component.defaultTravelAsFamily,
    destination: component.defaultDestination,
    region: component.defaultRegion,
    endDateOffsetDays: component.endDateOffsetDays,
  };
};

/**
 * TableBuilder's implementation of the calculated-attributes collection
 * contract (registered in `calculatedAttributes/collect.ts`): the table reports
 * the products in its rows, the attributes its columns are authored against,
 * and the vertical its mode puts it in. Which of those attributes are
 * calculated is the engine's call.
 *
 * Reporting its own vertical is what keeps a self-contained table working
 * anywhere: a Personal Loan table on a credit-card page or an article still
 * gets its figures, because the mode — not the page — says what it is
 * (CMS-1890 review, round 3). It is also why such a table ranks `statement`
 * and the reader's filter never moves it.
 */
export const collectTableBuilderCalculatedAttributes: CalculatedAttributesCollector<
  ComponentTableBuilder
> = (component) => {
  // A table hidden on both breakpoints paints nowhere, so it must not arm a
  // fetch on every render of the page that carries it.
  const isVisible = component.visibilityDesktop || component.visibilityMobile;
  if (!isVisible) return undefined;

  const mode = component.mode ?? 'GENERAL';
  const vertical = VERTICAL_BY_MODE[mode];
  // The vertical is reported even with nothing to calculate: the block's apply
  // CTA runs under it.
  if (vertical === NO_VERTICAL) return { vertical, groups: [] };

  // The loan terms are required in Personal Loan mode, but content can be out
  // of step with the schema (a mode switched after the fact), and the executor
  // has nothing to compute without them.
  if (
    mode === 'PERSONAL_LOAN' &&
    !(component.loanAmount && component.loanTenure)
  )
    return { vertical, groups: [] };

  // Only a catalogue-attribute column carries an attribute name; a free-text or
  // reward column draws something else entirely.
  const group: CollectedAttributeGroup = {
    productIds: component.rows
      .map((row) => row.identityCellProductRef?.title)
      .filter((title): title is string => !!title),
    attributeNames: component.columns
      .filter((column) => column?.type === 'CATALOGUE_ATTRIBUTE')
      .map((column) => column?.attributeKey?.name),
  };

  return {
    vertical,
    groups: [group],
    trip: getTableBuilderTripCriteria(component),
    loanFields:
      mode === 'PERSONAL_LOAN'
        ? { amount: component.loanAmount, tenure: component.loanTenure }
        : undefined,
  };
};
